import type { CSSProperties, ReactNode } from 'react';
import { fonts } from '../../theme/tokens';

export interface MonoProps {
  children?: ReactNode;
  /** Render element — `span` (default) for inline machine strings, `code` for
   *  code/log/YAML fragments, `div` for block preformatted content. */
  as?: 'span' | 'code' | 'div';
  /** Tabular figures so digits line up (IDs, counts, aligned metrics). */
  tabular?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Native tooltip — handy for truncated IDs. */
  title?: string;
}

/**
 * Mono — wraps a *machine string* in the monospace face: range / execution IDs,
 * scenario slugs, IPs, CIDRs, VM & hostnames, code, YAML, and log lines. Per
 * SYS-7 this is the ONLY sanctioned place for the mono font in product UI; all
 * chrome (headings, nav, card/panel/alert/modal titles, table headers, labels,
 * body copy) inherits the app sans face. For KPI numbers prefer `tabular`
 * (tabular-nums) over mono so figures align without the terminal look.
 */
export function Mono({ children, as = 'span', tabular = false, className, style, title }: MonoProps) {
  const Tag = as;
  return (
    <Tag
      {...(className !== undefined ? { className } : {})}
      {...(title !== undefined ? { title } : {})}
      style={{
        fontFamily: fonts.mono,
        ...(tabular ? { fontVariantNumeric: 'tabular-nums' } : {}),
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}
